import type * as Phaser from 'phaser';
import {
  visibleElevatedBodyIds,
  viewsWithin,
  type MapDocument,
  type ViewRect,
} from '../sim/map/index.ts';
import type { ElevatedBodyGraphicsPool } from './elevatedLayers.ts';
import type { IslandOverlayPool } from './islandOverlays.ts';
import { cameraWorldRect, screenToWorld } from './view.ts';

/**
 * Extra screen pixels around the view so a cliff rim or hint dash is shown
 * before it crosses the edge. The extruded AABB already covers the plateau lean.
 */
export const CULL_PAD_PX = 64;

/**
 * The cull is skipped while the padded view has moved less than this share of the pad
 * since the last cull. A body can therefore be at most half a pad late, still outside
 * the real viewport (docs/PERFORMANCE.md, Phase 3).
 */
export const CULL_SLACK_SHARE = 0.5;

export function columnVisibilityKey(ids: ReadonlySet<string>): string {
  return [...ids].sort().join('\0');
}

export function viewForColumnCull(cam: Phaser.Cameras.Scene2D.Camera): ViewRect {
  if (!(cam.zoom > 0) || cam.width <= 0 || cam.height <= 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const view = cameraWorldRect(cam.scrollX, cam.scrollY, cam.width, cam.height, cam.zoom);
  const pad = screenToWorld(CULL_PAD_PX, cam.zoom);

  return {
    x: view.x - pad,
    y: view.y - pad,
    width: view.width + pad * 2,
    height: view.height + pad * 2,
  };
}

export type ColumnVisibilityResult = {
  key: string;
  ids: ReadonlySet<string>;
  /** Padded world view the ids were computed for; hand it back as `lastView`. */
  view: ViewRect;
};

export function applyColumnVisibility(options: {
  map: MapDocument;
  cam: Phaser.Cameras.Scene2D.Camera;
  elevate: boolean;
  columns: ElevatedBodyGraphicsPool | null;
  overlays: IslandOverlayPool | null;
  lastKey: string;
  /** Previous result's ids / view; with both present a barely-moved camera skips the cull. */
  lastIds?: ReadonlySet<string> | null;
  lastView?: ViewRect | null;
  /** Force a recompute even when the view is still (map or elevation changed). */
  force?: boolean;
  restrokeHints: (visibleIds: ReadonlySet<string>) => void;
}): ColumnVisibilityResult {
  const view = viewForColumnCull(options.cam);
  const slack = screenToWorld(CULL_PAD_PX * CULL_SLACK_SHARE, options.cam.zoom);

  if (
    !options.force &&
    options.lastIds &&
    options.lastView &&
    options.lastKey !== '' &&
    viewsWithin(options.lastView, view, slack)
  ) {
    return { key: options.lastKey, ids: options.lastIds, view: options.lastView };
  }

  const ids = visibleElevatedBodyIds(options.map, view, options.elevate);
  const key = columnVisibilityKey(ids);

  if (key !== options.lastKey) {
    options.columns?.applyVisibility(ids);
    options.overlays?.applyVisibility(ids);
    options.restrokeHints(ids);
  }

  return { key, ids, view };
}
